import React, { useEffect, useMemo, useState } from "react"

import { shuffleArray } from "assets/helper/shuffle"
import { useExternalScripts } from "hooks/useExternalScripts"
import { useIntegrationsSettings } from "hooks/useIntegrationsSettings"
import { useSlider } from "components/testimonial/hooks/useSlider"

import { Slider } from "components/testimonial/Slider"
import { SliderController } from "components/testimonial/SliderController"
import { SliderPagination } from "components/testimonial/SliderPagination"
import { SwiperSlide } from "swiper/react"
import { Testimonial } from "components/testimonial/Testimonial"
import { TestimonialBanner } from "components/testimonial/TestimonialBanner"
import { TestimonialSkeleton } from "components/skeleton/Testimonial/TestimonialSkeleton"
import { BackgroundImage } from "components/testimonial/assets/BackgroundImage"

export type FieldroutesReviews = {
  city: string
  feedback: string
  formattedRating?: string
  profane: boolean
  rating: number
  reviewer: string
  state: string
}

type PestroutesClientResponse = {
  reviews: FieldroutesReviews[]
}

export const SingleReview = () => {
  const [reviews, setReviews] = useState<FieldroutesReviews[]>([])
  const [showReview, setShowReview] = useState(false)

  const {
    fieldroutesReviewImages,
    // fieldroutesReviewBackgroundImage,
    customHeadingFieldroutesReviews,
  } = useIntegrationsSettings()

  // const backgroundImageUrl = fieldroutesReviewBackgroundImage?.url ?? ""

  const avatarArray = useMemo(() => {
    const randomAvatarArray = shuffleArray(fieldroutesReviewImages)
    return randomAvatarArray
  }, [fieldroutesReviewImages])

  const {
    handleLoadingPRClientAndJQueryScripts,
    isPRClientAndJQueryScriptsLoaded,
    loadPestRoutesClient,
  } = useExternalScripts()

  /* eslint-disable */
  const getPestroutesClientReviews =
    async (): Promise<void | NodeJS.Timeout> => {
      // @ts-ignore: PestroutesClient was delayed imported
      if (typeof PestroutesClient !== "object") {
        return setTimeout(getPestroutesClientReviews, 100)
      }

      let result: PestroutesClientResponse

      try {
        // @ts-ignore: PestroutesClient was delayed imported
        result = await PestroutesClient?.getReviews({
          favorable: 1,
          minRating: 4,
          limit: 15,
          randomize: true,
        })
      } catch {
        return setTimeout(getPestroutesClientReviews, 100)
      }

      //remove reviews containing profanities
      const filteredReviews = result.reviews.filter(
        review => !review.profane && review.feedback.length > 15
      )
      const hasReviews = !filteredReviews || filteredReviews.length === 0

      if (hasReviews) {
        console.error(
          "There are no reviews from FieldRoutes for this website, please disable the option in the settings"
        )
        return
      }

      const reviewsWithRating = filteredReviews.map(randomSingleReview => {
        return {
          ...randomSingleReview,
          formattedRating: "★".repeat(Number(randomSingleReview.rating)),
        }
      })

      setReviews(reviewsWithRating)
      return
    }

  useEffect(() => {
    if (Object.keys(reviews).length > 0) {
      setShowReview(true)
    }
  }, [reviews])

  /* eslint-disable */
  useEffect(() => {
    let mounted = true

    if (isPRClientAndJQueryScriptsLoaded) {
      getPestroutesClientReviews()
      return
    }

    // avoids loading jquery before the LCP is done
    // @ts-ignore: PestroutesClient was delayed imported
    if (typeof PestroutesClient !== "object") {
      setTimeout(() => {
        if (mounted) {
          loadPestRoutesClient()
          handleLoadingPRClientAndJQueryScripts(true)
        }
      }, 4000)
    } else {
      handleLoadingPRClientAndJQueryScripts(true)
    }

    return () => {
      mounted = false
    }
  }, [isPRClientAndJQueryScriptsLoaded])

  const hasMoreThan1Review = reviews.length > 1

  const {
    activeIndex,
    handleNextSlide,
    handlePrevSlide,
    handlePagination,
    handleSetActiveIndex,
    handleSetSwiperInstance,
    handleSetSwiper2Instance,
    swiper1,
    swiper2,
  } = useSlider()

  const bgImageClass = "fr-testimonial__background-image"
  const testimonialWithSlider =
    showReview && hasMoreThan1Review
      ? `${bgImageClass} ${bgImageClass}--with-slider`
      : `${bgImageClass}`

  return (
    <TestimonialBanner
      hasFieldroutesReviews
      hasReviewLoaded={showReview}
      hasSliderWithNavigation={hasMoreThan1Review}
      heading={customHeadingFieldroutesReviews}
    >
      {showReview ? (
        <>
          <Slider
            controller={{ control: swiper2 }}
            onActiveIndexChange={swiper => {
              handleSetActiveIndex(swiper.activeIndex + 1)
            }}
            onSwiper={handleSetSwiperInstance}
          >
            <BackgroundImage className={testimonialWithSlider} />

            {reviews.map((review, index) => (
              <SwiperSlide key={index}>
                <Testimonial
                  hasFieldroutesReviews
                  avatar={avatarArray[index]?.single_image}
                  review={review?.feedback}
                  reviewerName={review?.reviewer}
                />
              </SwiperSlide>
            ))}
          </Slider>
          {hasMoreThan1Review && (
            <>
              <SliderPagination
                activeIndex={activeIndex}
                control={swiper1}
                handlePagination={handleSetSwiper2Instance}
                numberOfReviews={reviews.length}
                onPaginationClick={handlePagination}
              />
              <SliderController
                activeIndex={activeIndex}
                numberOfReviews={reviews.length}
                onSlideNext={handleNextSlide}
                onSlidePrev={handlePrevSlide}
              />
            </>
          )}
        </>
      ) : (
        <TestimonialSkeleton />
      )}
    </TestimonialBanner>
  )
}
